/**
 * The one classification the base-indicator layer and its legend share — spec §F5.3
 * (paint) and §F5.6 block (b) ("k swatch rows (numeric ranges … with fmt)").
 *
 * **Decision (NOTES #190): a hook, not a store slice.** §F5.6's value labels must be the
 * *same* `breaks` §F5.3 painted with, or the legend lies about the map. Two ways to share
 * them were on the table: have `Choropleth` write the computed breaks into a
 * `choroClasses` slice of the §F1 store, or compute them in one hook both components call.
 * The hook wins here because the inputs are already shared, cached server state — both
 * components ask TanStack Query for the *same* `['values', …]` and `['units', …]` keys, so
 * the second caller costs one `useMemo`, no extra fetch and no extra render pass. A store
 * slice would mean writing state from a render/effect of one component and reading it from
 * another, with a frame of disagreement between map and legend on every restyle, plus a
 * §F1 field the spec does not define. (M3's bivariate block will need its own hook beside
 * this one for the same reason.)
 */
import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import {
  fetchConfig,
  fetchIndicators,
  fetchUnits,
  fetchValues,
  modeFor,
  queryKeys,
  unitsQueryFor,
  type Domain,
  type Indicator,
  type MetaConfig,
  type UnitsFC,
  type ValueMode,
} from '../../lib/api';
import {
  selectScopeParent,
  selectYear,
  useAppStore,
  type ReportingLevel,
} from '../../lib/state';
import { autoRamp, classify, FALLBACK_RAMP_STOPS } from '../../lib/symbology';

export interface ChoroClasses {
  indicatorId: string;
  /** §D3 catalogue row (name, domain, fmt); null until `['indicators']` lands. */
  meta: Indicator | null;
  /** `/meta/config.domains` row for `meta.domain` — the legend's colour dot. */
  domain: Domain | null;
  /** Display unit, from the values header if it is there, else the catalogue row. */
  unitLabel: string;
  /** §G3 decimals. */
  fmt: number;
  year: number;
  /** `0|1|2`, or `'watershed'` for the §M3.5 overlay-hierarchy mode. */
  level: ReportingLevel;
  /** unit_id → value, for the units in the current scope (no-data units are absent). */
  values: Map<string, number>;
  units: UnitsFC | undefined;
  config: MetaConfig | undefined;
  /** `k - 1` upper bounds (§G1). Empty when there is nothing to classify. */
  breaks: number[];
  /** `k` hex colours (§G2), index-aligned with `classOf`. */
  colors: string[];
  /** The `/meta/config.ramps` key in force — `indStyle.ramp ?? autoRamp(…)`. */
  rampKey: string;
  /** The five stops of that ramp, before §G2 resampling to `indStyle.k`. */
  rampStops: string[];
  /**
   * §G4's `fillRampLastStop`: "last colour of the EFFECTIVE ramp (style override included)
   * — this is what makes overlays re-contrast when the user restyles the fill". Exposed
   * here rather than recomputed by `useOverlayClasses` so the colour the overlays contrast
   * *against* is by construction the colour the choropleth's darkest class is painted with.
   */
  fillRampLastStop: string;
  /** True once both server queries have landed. */
  ready: boolean;
  /** False when *no* unit in scope has a value for this year (§D3 omits them). */
  hasData: boolean;
  /**
   * True when the classes were spread over the **programme-wide** set rather than over the
   * units on screen — see `classificationBasis`. The §F5.6 legend prints a line saying so,
   * because the swatch ranges then describe more than the map does.
   */
  anchored: boolean;
  /**
   * The reading these numbers are — §F5.10's Cumulative checkbox as the *server* resolved
   * it. Read off the payload, not the store: during a held-over fetch the map is still
   * showing the previous mode's numbers, and the legend must describe what is painted.
   */
  mode: ValueMode;
}

/**
 * The values §G1 should actually classify over — client directive, 9 Sep 2026, item 2.
 *
 * > *"With a baseline variable mapped, filtering by country (or any operational-hierarchy
 * > rung) drops the original choropleth colouring."*
 *
 * It did, and the cause is arithmetic rather than plumbing. §F5.3's rule is that the breaks
 * describe *the displayed set*, and §G1's `quantBreaks` indexes a sorted array — so for a
 * single displayed unit all `k − 1` breaks are that one value, and `classOf`'s strict `>`
 * drops it into **class 0**, the palest stop of the ramp. Filtering to Burkina Faso took
 * *Agroforestry — manure or compost* from `#94C595` to `#EFF6EE`, one shade off the `#EEF1EC`
 * the map paints for *no data at all*, under a legend reading `≤ 9.9 / 9.9 – 9.9 / 9.9 – 9.9`.
 * Nothing was missing: the value was there, the level snap had found it, and the ramp was
 * still the domain's green. The classification had simply run out of units to spread over.
 *
 * **The threshold is `k` — the class count itself**, and it is the honest one rather than a
 * tuned one: `k` classes need at least `k` observations before quantiles mean anything, and
 * below that the breaks stop being a description of a distribution and start being an
 * artefact of the sort. At or above `k`, nothing changes — every roll-up indicator inside a
 * country still re-spreads over that country's own regions, which is what §F5.3 asks for and
 * what a reader comparing units within a filter wants.
 *
 * Below it, the classes are spread over the **programme-wide** values for the same indicator,
 * level, year and mode — one extra `/indicators/{id}/values` call with no `parent` and no
 * country, which is a payload the unfiltered explorer already fetches and caches under the
 * same key. The consequence is the one the client asked for: a unit keeps the colour it had
 * before the filter was applied, because it is being ranked against the same distribution it
 * was ranked against then.
 *
 * `programme` is ignored when it is not actually wider than what is on screen — a stale or
 * mismatched payload must never *narrow* the basis.
 */
export function classificationBasis(
  inScope: readonly number[],
  programme: readonly number[],
  k: number,
): { values: readonly number[]; anchored: boolean } {
  // Nothing on screen is the honest empty state, not a classification to widen: a region
  // filter over a survey estimate really has no rows, and the map says so in grey. Anchoring
  // there would paint five swatches over a legend describing units the reader cannot see.
  if (!inScope.length) return { values: inScope, anchored: false };
  if (inScope.length >= k || programme.length <= inScope.length) {
    return { values: inScope, anchored: false };
  }
  return { values: programme, anchored: true };
}

/**
 * Returns `null` while no indicator is mapped — which is exactly the §F5.6 auto-hide and
 * the §F5.3 "remove the layer" condition.
 */
export function useChoroplethClasses(): ChoroClasses | null {
  const indicatorId = useAppStore((s) => s.indicator);
  const level = useAppStore((s) => s.reportingLevel);
  const country = useAppStore((s) => s.country);
  const year = useAppStore(selectYear);
  const indStyle = useAppStore((s) => s.indStyle);
  const cumulative = useAppStore((s) => s.cumulative);
  /**
   * §F5.8's filter, on **both** queries.
   *
   * This is where "the numbers change" actually happens, and it happens for free: the units
   * query returns only the scope's subtree, `inScope` below is computed from those features,
   * and §G1 therefore classifies over the filtered set. The breaks re-spread, the legend
   * labels change with them, and neither this hook nor `classify` needed to learn what a
   * scope is — narrowing the input was the whole of it.
   */
  const parent = useAppStore(selectScopeParent);

  const on = Boolean(indicatorId);

  const { data: config } = useQuery({ queryKey: queryKeys.config(), queryFn: fetchConfig });
  const { data: indicators } = useQuery({
    queryKey: queryKeys.indicators(),
    queryFn: fetchIndicators,
    enabled: on,
  });
  // `keepPreviousData`: §F5.11 says "year change animates choropleth" — dropping to an
  // empty map for the length of a fetch on every 900 ms tick of the §F5.10 play loop is a
  // flicker, not an animation. The previous payload stays on screen until the new one
  // lands, and `usable` below throws it away when it would be *wrong* rather than merely
  // stale.
  // §F5.10's Cumulative checkbox, narrowed by the catalogue: an indicator that does not
  // accumulate keeps the annual key (and the annual URL) whatever the box says.
  const mode = modeFor(
    cumulative,
    indicators?.find((i) => i.id === indicatorId),
  );
  const { data: rawValues } = useQuery({
    queryKey: queryKeys.values(indicatorId ?? '', level, year, country, mode, parent),
    queryFn: () => fetchValues(indicatorId as string, level, year, country, mode, parent),
    enabled: on,
    placeholderData: keepPreviousData,
  });

  /**
   * A held-over payload is safe across a *year* or *country* change — values are keyed by
   * `unit_id`, so every row still describes the unit it names — but not across a **level**
   * change, where the previous ids belong to a different set of polygons entirely and
   * every new unit would flash as no-data, and certainly not across an **indicator**
   * change, where it would paint one indicator's numbers under another one's name. §D3
   * echoes both back, so either mismatch is detectable; when it happens the payload is
   * dropped (`ready` goes false) and the legend says "Loading…" until the real one lands.
   */
  const valuesData = useMemo(
    () =>
      rawValues &&
      rawValues.indicator.id === indicatorId &&
      (rawValues.level === null || rawValues.level === level)
        ? rawValues
        : undefined,
    [rawValues, indicatorId, level],
  );
  // §M3.5: at `level='watershed'` the polygons are the overlay hierarchy, not an admin
  // level — one helper decides that for the choropleth and the overlays alike, so the two
  // can never be drawn from different geometry.
  const unitsQuery = unitsQueryFor(level);
  const { data: units } = useQuery({
    queryKey: queryKeys.units(unitsQuery.type, unitsQuery.level, country, 'z7', parent),
    queryFn: () => fetchUnits(unitsQuery.type, unitsQuery.level, country, 'z7', parent),
    enabled: on,
  });

  const meta = useMemo(
    () => indicators?.find((i) => i.id === indicatorId) ?? null,
    [indicators, indicatorId],
  );

  const values = useMemo(() => {
    const m = new Map<string, number>();
    for (const row of valuesData?.values ?? []) m.set(row.unit_id, row.value);
    return m;
  }, [valuesData]);

  // Only the units actually on screen contribute to the classification (§F5.3: the breaks
  // describe the displayed set, which is why they move with country/level) — unless there
  // are too few of them to describe anything, which is `classificationBasis`.
  const inScope = useMemo(() => {
    const out: number[] = [];
    for (const f of units?.features ?? []) {
      const v = values.get(f.properties.id);
      if (typeof v === 'number' && Number.isFinite(v)) out.push(v);
    }
    return out;
  }, [units, values]);

  /**
   * The programme-wide reading of the same indicator, fetched **only** when the filtered map
   * has too few valued units to classify (see `classificationBasis`).
   *
   * Unfiltered it is never asked for: the payload above already *is* the programme, and the
   * key below is byte-for-byte the one it was fetched under, so under a filter this is
   * usually a cache hit rather than a request.
   */
  const filtered = parent !== null || country !== 'ALL';
  const anchorWanted =
    on && filtered && inScope.length > 0 && inScope.length < indStyle.k;
  const { data: rawAnchor, isError: anchorFailed } = useQuery({
    queryKey: queryKeys.values(indicatorId ?? '', level, year, 'ALL', mode, null),
    queryFn: () => fetchValues(indicatorId as string, level, year, 'ALL', mode, null),
    enabled: anchorWanted,
    staleTime: 30 * 60 * 1000,
    // The same §F5.11 hold-over the filtered query gets, and for the same reason: this
    // key moves with `year`, so inside an anchored scope every §F5.10 play tick would
    // otherwise open a one-round-trip gap with no anchor payload at all.
    placeholderData: keepPreviousData,
  });
  /**
   * An anchor that is wanted but not yet answered **holds the paint**. Painting without
   * it classifies the handful of visible values over themselves — a single watershed
   * lands in the palest class and the legend collapses to one number, which is the exact
   * colour loss the client reported and the reason `classificationBasis` exists. The
   * country rung rarely hits this (its anchor key is byte-identical to the unfiltered
   * fetch, so it is a cache hit); a scope that *changes the level* — a watershed pick —
   * asks under a key nothing has warmed, and on a slow stack that round-trip used to
   * paint one frame of wrong colours first.
   *
   * "Answered" is the echo check `valuesData` applies, not `isPending`: a held-over
   * previous-*year* payload (same indicator, a compatible level) is an answer — §F5.10's
   * play loop re-anchors from it instead of tearing the layer down on every tick — while
   * a held-over payload from another level or indicator is not one, and holds until the
   * real payload lands. A *failed* anchor releases the hold, so the map degrades to the
   * visible-set classes rather than an empty map.
   */
  const anchorUsable =
    rawAnchor !== undefined &&
    rawAnchor.indicator.id === indicatorId &&
    (rawAnchor.level === null || rawAnchor.level === level);
  const anchorHeld = anchorWanted && !anchorUsable && !anchorFailed;

  /** The anchor's numbers, subject to the same echo check `valuesData` applies. */
  const programme = useMemo(() => {
    if (!anchorWanted || !anchorUsable || !rawAnchor) return [] as number[];
    return rawAnchor.values
      .map((row) => row.value)
      .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  }, [anchorWanted, anchorUsable, rawAnchor]);

  return useMemo(() => {
    if (!indicatorId) return null;

    const rampKey = indStyle.ramp ?? autoRamp(indicatorId, meta?.domain ?? '');
    const stops = config?.ramps[rampKey] ?? config?.ramps.green ?? FALLBACK_RAMP_STOPS;
    const hasData = inScope.length > 0;
    const basis = classificationBasis(inScope, programme, indStyle.k);
    const { breaks, colors } = hasData
      ? classify([...basis.values], indStyle, stops)
      : // Nothing to classify: keep the k colours (so the style panel still previews the
        // ramp) but no breaks, and let the caller draw the §F5.3 no-data paint.
        { ...classify([0], indStyle, stops), breaks: [] };

    return {
      indicatorId,
      meta,
      domain: config?.domains.find((d) => d.id === meta?.domain) ?? null,
      unitLabel: valuesData?.indicator.unit ?? meta?.unit ?? '',
      fmt: valuesData?.indicator.fmt ?? meta?.fmt ?? 1,
      // The year **on screen**, which during a held-over fetch is still the previous one:
      // the legend header and the §F5.3 tooltip must date the numbers they are showing,
      // not the year the time bar has already moved to.
      year: valuesData?.year ?? year,
      level,
      values,
      units,
      config,
      breaks,
      colors,
      rampKey,
      rampStops: stops,
      // `sampleRamp` always keeps both endpoints, so `colors[k-1]` is this same string —
      // but only while `hasData`; read it off the ramp itself so the §G4 pick does not
      // change under the overlays the moment the base indicator has a year with no values.
      fillRampLastStop: stops[stops.length - 1] ?? FALLBACK_RAMP_STOPS[4]!,
      ready: Boolean(units && valuesData) && !anchorHeld,
      hasData,
      anchored: hasData && basis.anchored,
      mode: valuesData?.mode ?? 'annual',
    };
  }, [
    indicatorId,
    units,
    values,
    inScope,
    programme,
    anchorHeld,
    valuesData,
    config,
    meta,
    indStyle,
    year,
    level,
  ]);
}
